import { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { usePublicMenu, PublicProduct } from "@/hooks/usePublicMenu";
import { usePublicBanners } from "@/hooks/useRestaurantBanners";
import { usePublicCombos, ComboWithDetails } from "@/hooks/usePublicCombos";
import { usePublicCart, PublicComboSelection } from "@/hooks/usePublicCart";
import { usePublicOrder } from "@/hooks/usePublicOrder";
import { useTabletSession, TabletSession } from "@/hooks/useTabletSession";
import { useTabletIdleTimer } from "@/hooks/useTabletIdleTimer";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Components
import { TabletScreensaver } from "@/components/tablet/TabletScreensaver";
import { TabletSetupScreen } from "@/components/tablet/TabletSetupScreen";
import { TabletHeader } from "@/components/tablet/TabletHeader";
import { TabletSidebar, TabletSection } from "@/components/tablet/TabletSidebar";
import { TabletCategoryList } from "@/components/tablet/TabletCategoryList";
import { TabletProductGrid } from "@/components/tablet/TabletProductGrid";
import { TabletCart } from "@/components/tablet/TabletCart";
import { TabletHighlightsSection } from "@/components/tablet/TabletHighlightsSection";
import { TabletRatingScreen } from "@/components/tablet/TabletRatingScreen";
import { TabletBillSection } from "@/components/tablet/TabletBillSection";
import { TabletTableSelector } from "@/components/tablet/TabletTableSelector";
import { TabletProductDetailSheet, SelectedAddon } from "@/components/tablet/TabletProductDetailSheet";
import { PublicComboCard } from "@/components/public/PublicComboCard";
import { PublicComboDetailSheet } from "@/components/public/PublicComboDetailSheet";
import { LandscapeWarning } from "@/components/tablet/LandscapeWarning";
import { TabletKioskUnlockDialog } from "@/components/tablet/TabletKioskUnlockDialog";
import { useTabletKiosk } from "@/hooks/useTabletKiosk";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface TableData {
  id: string;
  number: string;
  name: string | null;
  status: string;
  current_order_id: string | null;
}

interface BillData {
  order_id: string;
  order_number: number;
  subtotal: number;
  discount: number;
  additional_fees: number;
  total: number;
  status: string;
  created_at: string;
}

interface OrderItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes: string | null;
}

export default function TabletMenuPage() {
  const { slug } = useParams<{ slug: string }>();
  const { restaurant, categories, products, isLoading, error } = usePublicMenu(slug || "", "tablet");
  const { banners } = usePublicBanners(restaurant?.id);
  const { data: combos = [] } = usePublicCombos(restaurant?.id, "tablet");
  const { session, hasSession, updateTableNumber, clearTabletSession, validateSessionStatus } = useTabletSession();
  const cart = usePublicCart();
  const { addTableOrderItem, isSubmitting } = usePublicOrder();
  const kiosk = useTabletKiosk();

  // States
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  // Local session to avoid race condition after setup
  const [localSession, setLocalSession] = useState<TabletSession | null>(null);
  
  // Use session from hook when available, fallback to local for immediate access after setup
  const effectiveSession = session || localSession;
  const [activeSection, setActiveSection] = useState<TabletSection>("highlights");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [showTableSelector, setShowTableSelector] = useState(false);
  const [showKioskUnlock, setShowKioskUnlock] = useState(false);
  const [tables, setTables] = useState<TableData[]>([]);
  const [bill, setBill] = useState<BillData | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [showScreensaver, setShowScreensaver] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PublicProduct | null>(null);
  const [productSheetOpen, setProductSheetOpen] = useState(false);
  const [selectedCombo, setSelectedCombo] = useState<ComboWithDetails | null>(null);
  const [comboSheetOpen, setComboSheetOpen] = useState(false);
  
  // Table change validation states
  const [pendingTableChange, setPendingTableChange] = useState<string | null>(null);
  const [showCodeValidation, setShowCodeValidation] = useState(false);
  const [validationCode, setValidationCode] = useState("");
  const [isValidating, setIsValidating] = useState(false);

  // Idle timer for screensaver
  const idleTimeout = 60; // Default 60 seconds
  const { isIdle, wakeUp } = useTabletIdleTimer({
    timeout: idleTimeout,
    onIdle: () => setShowScreensaver(true),
    enabled: isSetupComplete,
  });

  // Check if we have a valid session on mount - validate against database
  useEffect(() => {
    async function checkSession() {
      if (hasSession && session?.restaurantId === restaurant?.id) {
        // Verify session is still active in database
        const isValid = await validateSessionStatus();
        if (isValid) {
          setIsSetupComplete(true);
        } else {
          toast.info("Sessão anterior expirada. Faça o setup novamente.");
        }
      }
    }
    checkSession();
  }, [hasSession, session, restaurant, validateSessionStatus]);

  // Periodic session validation (every 2 minutes)
  useEffect(() => {
    if (!isSetupComplete) return;
    
    const checkInterval = setInterval(async () => {
        const isValid = await validateSessionStatus(effectiveSession?.waiterSessionId);
        if (!isValid) {
        toast.error("Sessão expirada. Faça o setup novamente.");
        cart.clearCart();
        setIsSetupComplete(false);
      }
    }, 2 * 60 * 1000); // 2 minutes
    
    return () => clearInterval(checkInterval);
  }, [isSetupComplete, validateSessionStatus, cart]);

  // Real-time listener for session revocation
  useEffect(() => {
    if (!session?.waiterSessionId || !isSetupComplete) return;

    const channel = supabase
      .channel(`tablet-session-${session.waiterSessionId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "waiter_work_sessions",
          filter: `id=eq.${session.waiterSessionId}`,
        },
        (payload) => {
          const newStatus = (payload.new as { status: string }).status;
          if (newStatus === "revoked" || newStatus === "expired") {
            toast.error("Sessão encerrada pelo gerente");
            cart.clearCart();
            clearTabletSession();
            setIsSetupComplete(false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session?.waiterSessionId, isSetupComplete, cart, clearTabletSession]);

  // Kiosk mode: discourage leaving the screen (best-effort on web)
  useEffect(() => {
    if (!kiosk.enabled) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // block common exit/utility shortcuts (best-effort)
      const key = e.key.toLowerCase();
      const isFunctionKey = key.startsWith("f") && key.length <= 3;
      if (e.altKey || e.metaKey || (e.ctrlKey && (key === "r" || key === "w" || key === "l"))) {
        e.preventDefault();
      }
      if (isFunctionKey) {
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("contextmenu", handleContextMenu);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [kiosk.enabled]);

  // Fetch tables for setup and selector
  useEffect(() => {
    async function fetchTables() {
      if (!restaurant) return;

      try {
        const { data } = await supabase.rpc("get_public_table", {
          p_restaurant_id: restaurant.id,
          p_table_number: "1",
        });

        // Fetch all tables
        const { data: tablesData } = await supabase
          .from("tables")
          .select("id, number, name, status, current_order_id")
          .eq("restaurant_id", restaurant.id)
          .eq("is_active", true)
          .order("number");

        if (tablesData) {
          setTables(tablesData);
        }
      } catch (err) {
        console.error("Error fetching tables:", err);
      }
    }

    fetchTables();
  }, [restaurant]);

  // Fetch bill data
  const fetchBillData = useCallback(async () => {
    // Use effectiveSession to handle race condition after setup
    const tableNumber = effectiveSession?.tableNumber;
    if (!restaurant || !tableNumber) return;

    try {
      // Find table and order
      const { data: tableData } = await supabase.rpc("get_public_table", {
        p_restaurant_id: restaurant.id,
        p_table_number: tableNumber,
      });

      if (tableData && tableData.length > 0 && tableData[0].current_order_id) {
        const orderId = tableData[0].current_order_id;

        const { data: billData } = await supabase.rpc("get_table_bill", {
          p_order_id: orderId,
        });

        if (billData && billData.length > 0) {
          setBill(billData[0] as BillData);
        }

        const { data: itemsData } = await supabase.rpc("get_table_order_items", {
          p_order_id: orderId,
        });

        if (itemsData) {
          setOrderItems(itemsData as OrderItem[]);
        }
      } else {
        setBill(null);
        setOrderItems([]);
      }
    } catch (err) {
      console.error("Error fetching bill:", err);
    }
  }, [restaurant, effectiveSession?.tableNumber]);

  useEffect(() => {
    if (isSetupComplete) {
      fetchBillData();
    }
  }, [isSetupComplete, fetchBillData]);

  // Handle setup complete
  const handleSetupComplete = (newSession: TabletSession) => {
    setLocalSession(newSession); // Save locally for immediate access
    setIsSetupComplete(true);
    setShowScreensaver(false);
  };

  // Handle table selection - opens code validation dialog
  const handleTableSelect = (tableNumber: string) => {
    if (tableNumber === effectiveSession?.tableNumber) {
      setShowTableSelector(false);
      return;
    }
    setPendingTableChange(tableNumber);
    setShowTableSelector(false);
    setShowCodeValidation(true);
  };

  // Validate code and confirm table change
  const handleConfirmTableChange = async () => {
    if (!validationCode || validationCode.length < 6 || !restaurant) return;
    
    setIsValidating(true);
    
    try {
      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("daily_access_codes")
        .select("code")
        .eq("restaurant_id", restaurant.id)
        .eq("valid_date", today)
        .maybeSingle();
      
      if (error || !data || data.code !== validationCode.toUpperCase()) {
        toast.error("Código inválido");
        setIsValidating(false);
        return;
      }
      
      // Code is valid - allow table change
      if (pendingTableChange) {
        updateTableNumber(pendingTableChange);
        fetchBillData();
        toast.success(`Mesa alterada para ${pendingTableChange}`);
      }
      
      // Clear states
      setShowCodeValidation(false);
      setValidationCode("");
      setPendingTableChange(null);
    } catch (err) {
      console.error("Error validating code:", err);
      toast.error("Erro ao validar código");
    } finally {
      setIsValidating(false);
    }
  };

  // Cancel table change
  const handleCancelTableChange = () => {
    setShowCodeValidation(false);
    setValidationCode("");
    setPendingTableChange(null);
  };

  // Handle add product - open detail sheet
  const handleAddProduct = (product: PublicProduct, notes?: string) => {
    setSelectedProduct(product);
    setProductSheetOpen(true);
  };

  // Handle add to cart from detail sheet
  const handleAddToCartFromSheet = (product: PublicProduct, quantity: number, notes: string, addons: SelectedAddon[]) => {
    cart.addItem(
      {
        id: product.id,
        name: product.name,
        price: product.price,
        image_url: product.image_url,
      },
      quantity,
      notes || undefined,
      addons.length > 0 ? addons : undefined
    );
  };

  // Handle remove product (by ID for simple cases)
  const handleRemoveProduct = (productId: string) => {
    const index = cart.items.findIndex((i) => i.id === productId);
    if (index >= 0) {
      if (cart.items[index].quantity > 1) {
        cart.updateQuantity(index, cart.items[index].quantity - 1);
      } else {
        cart.removeItem(index);
      }
    }
  };

  // Handle combo click
  const handleComboClick = (combo: ComboWithDetails) => {
    setSelectedCombo(combo);
    setComboSheetOpen(true);
  };

  // Handle add combo to cart
  const handleAddComboToCart = (
    combo: ComboWithDetails,
    price: number,
    selection: PublicComboSelection,
    quantity: number,
    notes: string
  ) => {
    cart.addComboItem(
      {
        id: combo.id,
        name: combo.name,
        combo_type: combo.combo_type,
        image_url: combo.image_url,
      },
      price,
      selection,
      quantity,
      notes || undefined
    );
    setComboSheetOpen(false);
  };

  // Handle checkout
  const handleCheckout = async () => {
    if (!restaurant || !effectiveSession?.tableNumber) {
      toast.error("Mesa não configurada");
      return;
    }

    if (!restaurant.customer_ordering_enabled) {
      toast.error("Pedidos pelo tablet estão desativados");
      return;
    }

    // Validate session before checkout - pass effectiveSession ID to avoid race condition
    const isValid = await validateSessionStatus(effectiveSession?.waiterSessionId);
    if (!isValid) {
      toast.error("Sessão expirada. Por favor, faça o setup novamente.");
      setIsSetupComplete(false);
      return;
    }

    const success = await addTableOrderItem({
      restaurantId: restaurant.id,
      tableNumber: effectiveSession.tableNumber,
      items: cart.items,
      comboItems: cart.comboItems,
      source: "tablet",
    });

    if (success) {
      cart.clearCart();
      fetchBillData();
      setActiveSection("bill");
      toast.success("Pedido enviado direto para a cozinha!");
    }
  };

  // Get item quantity from cart
  const getItemQuantity = (productId: string) => {
    const item = cart.items.find((i) => i.id === productId);
    return item?.quantity || 0;
  };

  // Filter products by category
  const filteredProducts =
    selectedCategory === "all"
      ? products
      : products.filter((p) => p.category_id === selectedCategory);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Error state
  if (error || !restaurant) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">
            Cardápio não disponível
          </h1>
          <p className="text-slate-400">{error || "Restaurante não encontrado"}</p>
        </div>
      </div>
    );
  }

  // Screensaver
  if (showScreensaver && isSetupComplete) {
    return (
      <TabletScreensaver
        banners={banners}
        intervalSeconds={5}
        onWakeUp={() => {
          setShowScreensaver(false);
          wakeUp();
        }}
        restaurantName={restaurant.name}
        restaurantLogo={restaurant.logo_url}
      />
    );
  }

  // Setup screen (needs waiter login and table selection)
  if (!isSetupComplete) {
    return (
      <TabletSetupScreen
        restaurant={restaurant}
        tables={tables}
        onSetupComplete={handleSetupComplete}
      />
    );
  }

  // Main tablet interface
  return (
    <>
      {/* Landscape orientation warning */}
      <LandscapeWarning />
    <div className="min-h-screen bg-slate-900">
      {/* Fixed Header */}
      <TabletHeader
        restaurantName={restaurant.name}
        restaurantLogo={restaurant.logo_url}
        tableNumber={effectiveSession?.tableNumber || "?"}
        cartItemCount={cart.itemCount}
        kioskEnabled={kiosk.enabled}
        onTableSettingsClick={() => {
          if (kiosk.enabled) {
            setShowKioskUnlock(true);
            return;
          }
          setShowTableSelector(true);
        }}
        onCartClick={() => setShowCart(true)}
      />

      {/* Fixed Sidebar */}
      <TabletSidebar
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        hasOpenBill={!!bill}
      />

      {/* Content area with padding to account for fixed header and sidebar */}
      <main className="pt-[68px] pl-24 min-h-screen overflow-y-auto">
          {activeSection === "highlights" && (
            <TabletHighlightsSection
              banners={banners}
              products={products}
              getItemQuantity={getItemQuantity}
              onAddProduct={handleAddProduct}
              onRemoveProduct={handleRemoveProduct}
              onNavigateToMenu={() => setActiveSection("menu")}
            />
          )}

          {activeSection === "menu" && (
            <div className="flex-1 overflow-auto p-6 space-y-6">
              {/* Category filter */}
              <TabletCategoryList
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
              />

              {/* Combos section */}
              {combos.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-white font-semibold text-lg">Combos</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    {combos
                      .filter((c) => selectedCategory === "all" || c.category_id === selectedCategory)
                      .map((combo) => (
                        <PublicComboCard
                          key={combo.id}
                          combo={combo}
                          onClick={() => handleComboClick(combo)}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Products grid */}
              <TabletProductGrid
                products={filteredProducts}
                getItemQuantity={getItemQuantity}
                onAddProduct={handleAddProduct}
                onRemoveProduct={handleRemoveProduct}
              />
            </div>
          )}

          {activeSection === "offers" && (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-slate-400 text-lg">
                Em breve: Ofertas especiais!
              </p>
            </div>
          )}

          {activeSection === "rating" && (
            <TabletRatingScreen
              restaurantId={restaurant.id}
              tableNumber={effectiveSession?.tableNumber || ""}
              orderId={bill?.order_id}
            />
          )}

          {activeSection === "bill" && (
            <TabletBillSection
              bill={bill}
              orderItems={orderItems}
              tableNumber={effectiveSession?.tableNumber || ""}
              restaurantId={restaurant.id}
              onRefresh={fetchBillData}
            />
          )}
        </main>

      {/* Cart */}
      {(cart.items.length > 0 || cart.comboItems.length > 0) && activeSection !== "bill" && (
        <TabletCart
          items={[
            ...cart.items.map((item) => ({
              ...item,
              image_url: products.find((p) => p.id === item.id)?.image_url,
            })),
            ...cart.comboItems.map((ci) => ({
              id: ci.comboId,
              name: ci.comboName,
              price: ci.price,
              quantity: ci.quantity,
              notes: ci.notes,
              image_url: ci.image_url,
            })),
          ]}
          subtotal={cart.subtotal}
          onUpdateQuantity={(index, quantity) => {
            if (index < cart.items.length) {
              cart.updateQuantity(index, quantity);
            } else {
              cart.updateComboQuantity(index - cart.items.length, quantity);
            }
          }}
          onRemove={(index) => {
            if (index < cart.items.length) {
              cart.removeItem(index);
            } else {
              cart.removeComboItem(index - cart.items.length);
            }
          }}
          onUpdateNotes={(index, notes) => {
            if (index < cart.items.length) {
              cart.updateItemNotes(index, notes);
            } else {
              cart.updateComboNotes(index - cart.items.length, notes);
            }
          }}
          onCheckout={handleCheckout}
          isSubmitting={isSubmitting}
          disabled={!restaurant.customer_ordering_enabled}
          isOpen={showCart}
          onOpenChange={setShowCart}
        />
      )}

      {/* Table selector dialog */}
      <Dialog open={showTableSelector} onOpenChange={setShowTableSelector}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl">
              Alterar Mesa
            </DialogTitle>
          </DialogHeader>
          <TabletTableSelector
            tables={tables}
            selectedTable={effectiveSession?.tableNumber || ""}
            onSelect={handleTableSelect}
          />
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowTableSelector(false)}
              className="border-slate-600"
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Kiosk unlock dialog (required to access admin actions) */}
      <TabletKioskUnlockDialog
        open={showKioskUnlock}
        onOpenChange={setShowKioskUnlock}
        onOpenAdmin={() => setShowTableSelector(true)}
      />

      {/* Code validation dialog for table change */}
      <Dialog open={showCodeValidation} onOpenChange={handleCancelTableChange}>
        <DialogContent className="max-w-sm bg-slate-900 border-slate-800">
          <DialogHeader>
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center">
              <Lock className="h-8 w-8 text-primary" />
            </div>
            <DialogTitle className="text-white text-xl text-center">
              Confirmar Troca de Mesa
            </DialogTitle>
            <p className="text-slate-400 text-sm text-center mt-2">
              Digite o Código do Dia para alterar para Mesa {pendingTableChange}
            </p>
          </DialogHeader>
          
          <form onSubmit={(e) => { e.preventDefault(); handleConfirmTableChange(); }} className="space-y-4">
            <Input
              type="text"
              placeholder="______"
              value={validationCode}
              onChange={(e) => setValidationCode(e.target.value.toUpperCase())}
              maxLength={6}
              className="text-center text-3xl tracking-[0.5em] font-mono uppercase bg-slate-900/50 border-slate-600 text-white h-16"
              disabled={isValidating}
              autoFocus
            />
            <p className="text-xs text-slate-500 text-center">
              Peça o código ao garçom ou gerente
            </p>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-slate-600"
                onClick={handleCancelTableChange}
                disabled={isValidating}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={validationCode.length < 6 || isValidating}
              >
                {isValidating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirmar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Product Detail Sheet */}
      <TabletProductDetailSheet
        product={selectedProduct}
        open={productSheetOpen}
        onOpenChange={setProductSheetOpen}
        onAddToCart={handleAddToCartFromSheet}
      />

      {/* Combo Detail Sheet */}
      <PublicComboDetailSheet
        combo={selectedCombo}
        open={comboSheetOpen}
        onOpenChange={setComboSheetOpen}
        onAddToCart={handleAddComboToCart}
      />
    </div>
    </>
  );
}
